import { useState, useEffect } from "react";
import { useGetPlayerByIdQuery } from "../store/api/mlbApi";
import { useGetNbaPlayerByIdQuery } from "../store/api/nbaApi";
import { useGetNflPlayerByIdQuery } from "../store/api/nflApi";
import api from "../utils/api";
import { isAlmanacPlayerId, sportFetchesHeadshots } from "../utils/playerHeadshots";

/**
 * Custom hook for managing player photos
 * Handles loading states, fallbacks, and error handling for player headshots
 *
 * @param {string} playerId - The player ID to get photos for
 * @param {Object} options - Configuration options
 * @param {number} options.expiration - URL expiration time in seconds (default: 3600)
 * @param {boolean} options.enabled - Whether to fetch photos (default: true)
 * @returns {Object} Photo data and loading states
 */
export const usePlayerPhoto = (playerId, options = {}) => {
  const { expiration = 3600, enabled = true, sport = "mlb" } = options;

  const [photoData, setPhotoData] = useState({
    url: null,
    hasPhoto: false,
    isGeneric: true,
    r2Key: null,
    contentHash: null,
    metadata: null,
    downloadedAt: null,
    error: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const isMlb = sport === "mlb";
  const isNba = sport === "nba";
  const isNfl = sport === "nfl";
  const canFetchPlayer = enabled && sportFetchesHeadshots(sport, playerId);

  const {
    data: mlbPlayer,
    isLoading: mlbPlayerLoading,
    error: mlbPlayerError,
  } = useGetPlayerByIdQuery(playerId, {
    skip: !canFetchPlayer || !isMlb,
  });

  const {
    data: nbaPlayer,
    isLoading: nbaPlayerLoading,
    error: nbaPlayerError,
  } = useGetNbaPlayerByIdQuery(playerId, {
    skip: !canFetchPlayer || !isNba,
  });

  const {
    data: nflPlayer,
    isLoading: nflPlayerLoading,
    error: nflPlayerError,
  } = useGetNflPlayerByIdQuery(playerId, {
    skip: !canFetchPlayer || !isNfl,
  });

  const player = isNfl ? nflPlayer : isNba ? nbaPlayer : mlbPlayer;
  const playerLoading = isNfl
    ? nflPlayerLoading
    : isNba
      ? nbaPlayerLoading
      : mlbPlayerLoading;
  const playerError = isNfl
    ? nflPlayerError
    : isNba
      ? nbaPlayerError
      : mlbPlayerError;

  useEffect(() => {
    if (!canFetchPlayer) {
      return;
    }

    if (playerLoading) {
      return;
    }

    if (playerError) {
      setError(`Failed to load player data: ${playerError.message}`);
      return;
    }

    if (!player) {
      setError("Player not found");
      return;
    }

    const fetchPhotoUrl = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const photoPath =
          sport === "nba"
            ? `/nba/players/${playerId}/photos?expiration=${expiration}`
            : sport === "nfl"
              ? `/nfl/players/${playerId}/photos?expiration=${expiration}`
              : `/players/${playerId}/photos?expiration=${expiration}`;

        const response = await api.get(photoPath);

        const data = response.data;

        if (data.headshot) {
          setPhotoData({
            url: data.headshot.url,
            hasPhoto: data.headshot.hasPhoto,
            isGeneric: data.headshot.isGeneric,
            r2Key: data.headshot.r2Key,
            contentHash: data.headshot.contentHash,
            metadata: data.headshot.metadata,
            downloadedAt: data.headshot.downloadedAt,
            error: data.headshot.error || null,
          });
        } else {
          throw new Error("No headshot data received from API");
        }
      } catch (err) {
        console.error("Failed to fetch player photo:", err);
        setError(err.message);

        // Set fallback data on error
        setPhotoData({
          url: null,
          hasPhoto: false,
          isGeneric: true,
          r2Key: "generic/headshot/1.jpg",
          contentHash: null,
          metadata: null,
          downloadedAt: null,
          error: err.message,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPhotoUrl();
  }, [playerId, canFetchPlayer, player, playerLoading, playerError, expiration, sport]);

  return {
    // Photo data
    photoData,

    // Loading states
    isLoading: isLoading || playerLoading,
    isPhotoLoading: isLoading,
    isPlayerLoading: playerLoading,

    // Error states
    error,
    hasError: !!error,

    // Computed properties
    hasPhoto: photoData.hasPhoto && !photoData.error,
    isGeneric: photoData.isGeneric,
    photoUrl: photoData.url,

    // Metadata
    metadata: photoData.metadata,
    downloadedAt: photoData.downloadedAt,
    contentHash: photoData.contentHash,
    r2Key: photoData.r2Key,
  };
};

/**
 * Hook for getting all player photos (not just headshot)
 *
 * @param {string} playerId - The player ID to get photos for
 * @param {Object} options - Configuration options
 * @param {number} options.expiration - URL expiration time in seconds (default: 3600)
 * @param {boolean} options.enabled - Whether to fetch photos (default: true)
 * @returns {Object} All photo data and loading states
 */
export const usePlayerPhotos = (playerId, options = {}) => {
  const { expiration = 3600, enabled = true } = options;

  const [photosData, setPhotosData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get player data to check if photos exist
  const {
    data: player,
    isLoading: playerLoading,
    error: playerError,
  } = useGetPlayerByIdQuery(playerId, {
    skip: !enabled || !isAlmanacPlayerId(playerId),
  });

  useEffect(() => {
    if (!enabled || !isAlmanacPlayerId(playerId) || playerLoading) {
      return;
    }

    if (playerError) {
      setError(`Failed to load player data: ${playerError.message}`);
      return;
    }

    if (!player) {
      setError("Player not found");
      return;
    }

    // Fetch all photos from API
    const fetchAllPhotos = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.get(
          `/players/${playerId}/photos/all?expiration=${expiration}`
        );

        const data = response.data;

        if (data.photos) {
          setPhotosData(data.photos);
        } else {
          throw new Error("No photos data received from API");
        }
      } catch (err) {
        console.error("Failed to fetch player photos:", err);
        setError(err.message);
        setPhotosData({});
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllPhotos();
  }, [playerId, enabled, player, playerLoading, playerError, expiration]);

  return {
    // Photos data
    photos: photosData,

    // Loading states
    isLoading: isLoading || playerLoading,
    isPhotosLoading: isLoading,
    isPlayerLoading: playerLoading,

    // Error states
    error,
    hasError: !!error,

    // Computed properties
    hasPhotos: Object.keys(photosData).length > 0,
    photoTypes: Object.keys(photosData),

    // Individual photo accessors
    headshot: photosData.headshot || null,
    action: photosData.action || null,
    portrait: photosData.portrait || null,
  };
};
